import { useTranslation } from '@dreamstack/i18n'
import filter from 'lodash/filter'
import first from 'lodash/first'
import groupBy from 'lodash/groupBy'
import { useMemo } from 'react'
import { angebote_data } from '../data/angebote_data'
import { REGION_TILES_DATA } from '../data/regionTiles_data'
import { calculateRelativeChange, stripQuarters } from './helperFunctions'

export type AverageTileData = {
  label: string // TODO: localize
  value: number
  unit?: string // TODO: loclaize
  relativeChange: number
  formatType?: 'integer' | 'decimal'
  totalValue?: number
  relativeChangeTotal?: number
}

export type AverageTileRAWData = {
  name: string
  mittl_preis_qm_kauf_bestand: number
  mittl_preis_qm_kauf_neubau: number
  mittl_preis_qm_miete_bestand: number
  mittl_preis_qm_miete_neubau: number
  _angebote_kauf_bestand: number
  _angebote_kauf_neubau: number
  _angebote_miete_bestand: number
  _angebote_miete_neubau: number
  zeit: string
}

export const useDataForAverageTiles = (region: string) => {
  const t = useTranslation()
  const parsed_Data = useMemo(() => {
    const data = REGION_TILES_DATA as AverageTileRAWData[]

    const filteredForRegion = filter(data, (d) => d.name === region)
    const filteredForRegion_angebote = filter(
      angebote_data,
      (d) => d.name === region
    )

    const strippedQuarters = stripQuarters(filteredForRegion)
    const strippedQuarters_angebote = stripQuarters(filteredForRegion_angebote)

    const groupedByYear = groupBy(strippedQuarters, 'zeit')
    const groupedByYear_angebote = groupBy(strippedQuarters_angebote, 'zeit')

    const avg_data = Object.keys(groupedByYear).map((year) => {
      const groupedByQuarter = groupedByYear[year]
      const groupedByQuarter_angebote = groupedByYear_angebote[year]
      return {
        year: year,
        avg_rent_price_neubau:
          groupedByQuarter.reduce(
            (acc, cur) => acc + cur.mittl_preis_qm_miete_neubau,
            0
          ) / groupedByQuarter.length,
        avg_rent_price_bestand:
          groupedByQuarter.reduce(
            (acc, cur) => acc + cur.mittl_preis_qm_miete_bestand,
            0
          ) / groupedByQuarter.length,
        avg_buy_price_neubau:
          groupedByQuarter.reduce(
            (acc, cur) => acc + cur.mittl_preis_qm_kauf_neubau,
            0
          ) / groupedByQuarter.length,
        avg_buy_price_bestand:
          groupedByQuarter.reduce(
            (acc, cur) => acc + cur.mittl_preis_qm_kauf_bestand,
            0
          ) / groupedByQuarter.length,
        angebote_miete_neubau:
          groupedByQuarter.reduce(
            (acc, cur) => acc + cur._angebote_miete_neubau,
            0
          ) / groupedByQuarter.length,
        angebote_miete_bestand:
          groupedByQuarter.reduce(
            (acc, cur) => acc + cur._angebote_miete_bestand,
            0
          ) / groupedByQuarter.length,
        angebote_kauf_neubau:
          groupedByQuarter.reduce(
            (acc, cur) => acc + cur._angebote_kauf_neubau,
            0
          ) / groupedByQuarter.length,
        angebote_kauf_bestand:
          groupedByQuarter.reduce(
            (acc, cur) => acc + cur._angebote_kauf_bestand,
            0
          ) / groupedByQuarter.length,
        angebote_kauf_gesamt: groupedByQuarter_angebote.reduce(
          (acc, cur) => acc + cur._angebote_kauf,
          0
        ),
        angebote_miete_gesamt: groupedByQuarter_angebote.reduce(
          (acc, cur) => acc + cur._angebote_miete,
          0
        ),
      }
    })

    const avg_data_2023 = first(avg_data.filter((d) => d.year === '2023'))
    const avg_data_2021 = first(avg_data.filter((d) => d.year === '2021'))

    if (!avg_data_2023 || !avg_data_2021) {
      console.warn('could not calculate average', {
        avg_data_2023,
        avg_data_2021,
        avg_data,
      })
      return []
    }

    const parsed_Data: AverageTileData[] = [
      // Bestand
      {
        label: t(
          'accentro:districtLandingPageGraphs.regionAverageGraph.averageRentPrices'
        ),
        value: avg_data_2023.avg_rent_price_bestand,
        unit: 'EUR / m²',
        relativeChange: calculateRelativeChange(
          avg_data_2021.avg_rent_price_bestand,
          avg_data_2023.avg_rent_price_bestand
        ),
      },
      {
        label: t(
          'accentro:districtLandingPageGraphs.regionAverageGraph.averagePurchasePrices'
        ),
        value: avg_data_2023.avg_buy_price_bestand,
        unit: 'EUR / m²',
        relativeChange: calculateRelativeChange(
          avg_data_2021.avg_buy_price_bestand,
          avg_data_2023.avg_buy_price_bestand
        ),
      },
      {
        label: t(
          'accentro:districtLandingPageGraphs.regionAverageGraph.rentalOfferts'
        ),
        value: avg_data_2023.angebote_miete_bestand,
        relativeChange: calculateRelativeChange(
          avg_data_2021.angebote_miete_bestand,
          avg_data_2023.angebote_miete_bestand
        ),
        totalValue: avg_data_2023.angebote_miete_gesamt,
        relativeChangeTotal: calculateRelativeChange(
          avg_data_2021.angebote_miete_gesamt,
          avg_data_2023.angebote_miete_gesamt
        ),
      },
      {
        label: t(
          'accentro:districtLandingPageGraphs.regionAverageGraph.saleOffers'
        ),
        value: avg_data_2023.angebote_kauf_bestand,
        relativeChange: calculateRelativeChange(
          avg_data_2021.angebote_kauf_bestand,
          avg_data_2023.angebote_kauf_bestand
        ),
        totalValue: avg_data_2023.angebote_kauf_gesamt,
        relativeChangeTotal: calculateRelativeChange(
          avg_data_2021.angebote_kauf_gesamt,
          avg_data_2023.angebote_kauf_gesamt
        ),
      },
      // Neubau
      {
        label: t(
          'accentro:districtLandingPageGraphs.regionAverageGraph.averageRentPrices'
        ),
        value: avg_data_2023.avg_rent_price_neubau,
        unit: 'EUR / m²',
        relativeChange: calculateRelativeChange(
          avg_data_2021.avg_rent_price_neubau,
          avg_data_2023.avg_rent_price_neubau
        ),
        formatType: 'decimal',
      },
      {
        label: t(
          'accentro:districtLandingPageGraphs.regionAverageGraph.averagePurchasePrices'
        ),
        value: avg_data_2023.avg_buy_price_neubau,
        unit: 'EUR / m²',
        relativeChange: calculateRelativeChange(
          avg_data_2021.avg_buy_price_neubau,
          avg_data_2023.avg_buy_price_neubau
        ),
        formatType: 'integer',
      },
      {
        label: t(
          'accentro:districtLandingPageGraphs.regionAverageGraph.rentalOfferts'
        ),
        value: avg_data_2023.angebote_miete_neubau,
        relativeChange: calculateRelativeChange(
          avg_data_2021.angebote_miete_neubau,
          avg_data_2023.angebote_miete_neubau
        ),
        totalValue: avg_data_2023.angebote_miete_gesamt,
        relativeChangeTotal: calculateRelativeChange(
          avg_data_2021.angebote_miete_gesamt,
          avg_data_2023.angebote_miete_gesamt
        ),
      },
      {
        label: t(
          'accentro:districtLandingPageGraphs.regionAverageGraph.saleOffers'
        ),
        value: avg_data_2023.angebote_kauf_neubau,
        relativeChange: calculateRelativeChange(
          avg_data_2021.angebote_kauf_neubau,
          avg_data_2023.angebote_kauf_neubau
        ),
        totalValue: avg_data_2023.angebote_kauf_gesamt,
        relativeChangeTotal: calculateRelativeChange(
          avg_data_2021.angebote_kauf_gesamt,
          avg_data_2023.angebote_kauf_gesamt
        ),
      },
    ]
    return parsed_Data
  }, [region, t])

  return parsed_Data
}
